import type { StoreApi } from 'zustand/vanilla';
import { combine, devtools } from 'zustand/middleware';
import { uuidv4 } from 'utils/id-utils';
import type { ToastMessageIdEnum } from 'stores/useToastsStore/toastMessages';
import type { IntlShape } from 'react-intl';
import type { ReactNode } from 'react';
import { createWithEqualityFn } from 'zustand/traditional';

type ToastPlacement = 'left' | 'center' | 'right';
type ToastType = 'warning' | 'danger' | 'success' | 'info' | 'primary';

interface BaseToast {
  id?: string;
  timeout?: number;
  placement?: ToastPlacement;
  dismissible?: boolean;
}

interface ComponentToast extends BaseToast {
  Component: React.FunctionComponent<any>;
}

type SavedComponentToast = ComponentToast & { id: string };

interface SimpleToast extends BaseToast {
  contents: ReactNode;
  type?: ToastType;
}

type SavedSimpleToast = SimpleToast & { id: string };

// This is the second parameter of formatMessage for values
type ToastValues = Parameters<IntlShape['formatMessage']>[1];

interface KeyedToast extends BaseToast {
  message: ToastMessageIdEnum;
  values?: ToastValues;
  type?: ToastType;
}

type SavedKeyedToast = KeyedToast & { id: string };

type ToastInput = SimpleToast | ComponentToast | KeyedToast;
type SavedToast = SavedComponentToast | SavedSimpleToast | SavedKeyedToast;

interface ToastsState {
  toasts: {
    [key: string]: SavedToast;
  };
}

const initialState: ToastsState = {
  toasts: {},
};

const toastsActions = (set: StoreApi<ToastsState>['setState']) => ({
  addToast: (toast: ToastInput): string => {
    const newToast = {
      ...toast,
      id: toast.id ?? uuidv4(),
      placement: toast.placement ?? 'left',
    };

    set((state) => ({
      toasts: {
        ...state.toasts,
        [newToast.id]: newToast,
      },
    }));
    return newToast.id;
  },
  removeToast: (id: string): void => {
    set((state) => {
      const { [id]: without, ...toasts } = state.toasts;
      return {
        toasts,
      };
    });
  },
});

const useToastsStore = createWithEqualityFn(
  devtools(combine(initialState, toastsActions), { name: 'ToastsStore' })
);

const isComponentToast = (toast: SavedToast): toast is SavedComponentToast => {
  return (toast as SavedComponentToast).Component !== undefined;
};

const isSimpleToast = (toast: SavedToast): toast is SavedSimpleToast => {
  return (toast as SavedSimpleToast).contents !== undefined;
};

const isKeyedToast = (toast: SavedKeyedToast): toast is SavedKeyedToast => {
  return toast.message !== undefined;
};

export const reset = () => {
  useToastsStore.setState(initialState);
};

export default useToastsStore;
export { isSimpleToast, isKeyedToast, isComponentToast };
export type { SavedToast, ToastInput, ToastType };
